import { defineElement, FC, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button, ButtonA } from '@atoms/button/button';
import { BPDFunctionalSvg } from '@atoms/svg/svg';
import { AREA_PICKER_MODAL_ID } from '@constants/modals';
import { openModal } from '@store/modals';
import { logout } from '@store/user';
import { GTMPush } from '@utilities/gtm';
import { useStylesheet, useTranslate } from '@utilities/hooks';
import { Link } from '@utilities/types';

import style from './profile-dropdown.scss';

const ProfileDropdown: FC = () => {
    useStylesheet(style);

    const [useMehNavigation] = useProp<boolean>('useMehNavigation', false); // feature flag
    const [hasUserAreas] = useProp<boolean>('hasUserAreas', false); // feature flag

    const [email] = useProp<string>('email', '');
    const [fullName] = useProp<string>('fullName', '');
    const [mijnHomeUrl] = useProp<string>('mijnHomeUrl', '');
    const [mijnAccountUrl] = useProp<string>('mijnAccountUrl', '');
    const [accountNavigationLinks] = useProp<string>('accountNavigationLinks', '');

    const links: Link[] = JSON.parse(accountNavigationLinks);

    const handleGtm = (data: { eventLabel?: string }) => {
        GTMPush({
            event: 'menuUsed',
            eventCategory: 'Menu',
            eventAction: 'Navigatie gebruikt',
            eventLabel: data.eventLabel,
        });
    };

    const handleLogout = () => {
        handleGtm({ eventLabel: 'logout' });
        logout();
    };

    const openSitePicker = () => {
        handleGtm({ eventLabel: 'to other website' });
        openModal(AREA_PICKER_MODAL_ID);
    };

    return useMehNavigation ? (
        <div class="c-profile-dropdown">
            <div class="profile-dropdown__inner">
                <div class="profile-dropdown__account-info">
                    <span class="u-body-normal-bold u-color--grey-50">{fullName}</span>
                    <span class="u-body-normal u-color--grey-30">{email}</span>
                    <ButtonA
                        classes="profile-dropdown__cta u-margin-top-1"
                        label={useTranslate('Settings')}
                        url={mijnAccountUrl}
                        size="small"
                        onClick={() => handleGtm({ eventLabel: 'settings' })}
                    />
                </div>
                <ul class="profile-dropdown__list">
                    {links.map(({ url, label, gtmLabel }) => (
                        <li class="profile-dropdown__list-item">
                            <a href={url} onClick={() => handleGtm({ eventLabel: gtmLabel })}>
                                <span class="u-body-normal u-color--grey-50">{label}</span>
                            </a>
                        </li>
                    ))}
                </ul>
                <div class="profile-dropdown__actions-bar u-color--grey-50">
                    {hasUserAreas ? (
                        <Button
                            classes="profile-dropdown__cta"
                            label={useTranslate('ToOtherWebsite')}
                            variant="grey-5"
                            size="small"
                            onClick={openSitePicker}
                        />
                    ) : null}
                    <Button
                        classes="profile-dropdown__cta"
                        label={useTranslate('LogOut')}
                        variant="grey-5"
                        size="small"
                        onClick={handleLogout}
                    />
                </div>
            </div>
        </div>
    ) : (
        <ul class="bpd-profile-dropdown__list">
            <li class="bpd-profile-dropdown__list-item">
                <a href={mijnHomeUrl} class="bpd-profile-dropdown__list-link">
                    {useTranslate('My')} {window.__ENVIRONMENT_DETAILS__.areaProjectName}
                </a>
            </li>
            <li class="bpd-profile-dropdown__list-item">
                <a href={mijnAccountUrl} class="bpd-profile-dropdown__list-link">
                    <span class="bpd-profile-dropdown__name">{fullName}</span>
                    <span class="bpd-profile-dropdown__email">{email}</span>
                </a>
            </li>
            <li class="bpd-profile-dropdown__list-item bpd-profile-dropdown__list-actions">
                <a href={mijnAccountUrl} class="bpd-profile-dropdown__list-link">
                    <BPDFunctionalSvg name="icons-filter" />
                    <span>{useTranslate('Account')}</span>
                </a>
                <button class="bpd-profile-dropdown__list-link" onClick={logout}>
                    {useTranslate('LogOut')}
                </button>
            </li>
        </ul>
    );
};

ProfileDropdown.props = {
    useMehNavigation: { type: Boolean }, // feature-flag
    hasUserAreas: { type: Boolean },
    email: { type: String },
    fullName: { type: String },
    mijnHomeUrl: { type: String },
    mijnAccountUrl: { type: String },
    accountNavigationLinks: { type: String },
    sitePickerOpen: { type: String },
};

defineElement('bpd-profile-dropdown', ProfileDropdown);
